'use client';
import { ReactNode } from 'react';
import { useIntlGlobalData, useMedia } from '@ssv-website/hooks';
import {
  ChatIcon,
  ChevronDownIcon,
  Icon,
  PoweroffIcon,
} from 'tdesign-icons-react';
import { Button, Dropdown, DropdownProps, Image } from 'tdesign-react';
import {
  getConferenceChatAuthApiBySite,
  openConferenceChatEntry,
} from '../../../navbar/conferenceChat';
import { UserCircleIcon } from '../../s-2026-signup/icons';

export default function SSVNavbar({
  className,
  mobileMenuIcon,
  onLogout,
  userMenuVariant = 'default',
}: {
  className?: string;
  mobileMenuIcon?: ReactNode;
  onLogout?: (logout: () => Promise<boolean>) => void;
  userMenuVariant?: 'default' | 'conference-signup';
}) {
  const { userInfo, isLogin = false, logout, login } = useIntlGlobalData();
  const { firstName, lastName, email } = userInfo || {};
  const enName = (firstName || '') + (lastName || '');

  const isMobile = useMedia('mobile');
  const isConferenceSignupMenu = userMenuVariant === 'conference-signup';
  const isConferenceSignupMobileMenu =
    isConferenceSignupMenu && isMobile;
  const userMenuActions = [
    {
      icon: <ChatIcon size={16} />,
      label: 'Chat',
      value: 'conference-chat',
    },
    {
      icon: <PoweroffIcon size={16} />,
      label: 'Log Out',
      value: 1,
    },
  ];
  const options = userMenuActions.map((action, index) => ({
    content: isConferenceSignupMobileMenu ? (
      <div className="text-base leading-[1.8] text-center">
        {action.label}
      </div>
    ) : (
      <div className="flex items-center justify-end gap-2 text-base leading-[1.8] text-right">
        {index === 0 ? action.icon : null}
        <span>{action.label}</span>
      </div>
    ),
    value: action.value,
  }));
  const handleMenuClick = (value: unknown) => {
    if (value === 'conference-chat') {
      // 英文站个人菜单只在登录后展示；进入会聊时只调用海外 BFF 鉴权。
      void openConferenceChatEntry({
        authApi: getConferenceChatAuthApiBySite('en'),
        site: 'en',
      });
      return;
    }
    if (value === 1) {
      if (onLogout) {
        onLogout(logout);
        return;
      }
      void logout().then((success) => {
        if (
          success &&
          window.location.pathname === '/case/activity/summit'
        ) {
          window.location.replace(window.location.href);
        }
      });
    }
  };
  const clickHandler: DropdownProps['onClick'] = (data) => {
    handleMenuClick(data?.value);
  };

  return (
    <div
      className={`sticky w-full top-0 z-[99] text-[#121C59] bg-white mobile:fixed ${className}`}
    >
      <div className="max-w-[1160px] h-[68px] mobile:w-full mobile:px-6 mobile:h-[48px] flex items-center justify-between mx-auto">
        <Image
          src={'https://cdn-digital.ssv.qq.com/upload//s2025/intl-ssv-logo.png'}
          className="w-[177px] h-[28px]"
          loading="lazy"
          style={{
            backgroundColor: 'transparent',
          }}
          alt="SSV"
        ></Image>

        {!isLogin ? (
          <div className="flex items-center gap-3">
            <Button variant="text" className="!text-base" onClick={login}>
              Log in
            </Button>
          </div>
        ) : isConferenceSignupMenu && !isMobile ? (
          <div className="group relative">
            <div className="cursor-pointer overflow-hidden rounded-[4px] group-hover:overflow-visible">
              <div className="inline-flex items-center gap-2 rounded-[4px] px-[6px] py-[5px] text-[rgba(0,0,0,0.9)] transition-colors duration-200 group-hover:bg-[#F3F5F7]">
                <UserCircleIcon />
                <span className="max-w-[120px] truncate text-sm leading-[22px]">
                  {enName || email || '--'}
                </span>
                <ChevronDownIcon size={16} />
              </div>
            </div>
            <div className="pointer-events-none absolute right-0 top-full z-20 pt-3 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
              <div className="min-w-[154px] rounded-[8px] bg-white py-4 shadow-card">
                {userMenuActions.map((action) => (
                  <div
                    key={action.value}
                    className="relative flex cursor-pointer items-center gap-2 px-5 py-2 text-left text-sm leading-6 text-[#121212] transition-colors duration-200 hover:text-[#0052D9] hover:before:absolute hover:before:left-0 hover:before:top-0 hover:before:h-full hover:before:w-px hover:before:bg-[#0A65E0] hover:before:content-['']"
                    onClick={() => handleMenuClick(action.value)}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Dropdown
            direction="right"
            hideAfterItemClick
            onClick={clickHandler}
            options={options}
            placement="bottom-right"
            minColumnWidth={'auto'}
            trigger={isMobile ? 'click' : 'hover'}
            popupProps={
              isConferenceSignupMenu ? undefined : { showArrow: true }
            }
            panelTopContent={
              isMobile && !isConferenceSignupMenu && (
                <div className="text-base leading-[2] text-[#121c59]/40 border-b border-[#f2f3ff] text-center pb-2 max-w-[150px] line-clamp-1">
                  {enName || email}
                </div>
              )
            }
          >
            {!isMobile ? (
              // <Button
              //   variant="text"
              //   className="block !text-base max-w-[180px] line-clamp-1 text-[#121c59]"
              //   suffix={<Icon name="chevron-down" size="16" />}
              // >

              //   {enName || email}
              // </Button>
              <div className="flex items-center gap-2 cursor-pointer overflow-hidden">
                <div className="!text-base max-w-[180px] line-clamp-1 text-[#121c59]">
                  {enName || email}
                </div>
                <Icon name="chevron-down" size="16" />
              </div>
            ) : isConferenceSignupMenu ? (
              <div className="inline-flex items-center gap-1 text-[rgba(0,0,0,0.9)]">
                <UserCircleIcon />
                <span className="max-w-[100px] truncate text-sm leading-[22px]">
                  {enName || email || '--'}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="text-[rgba(0,0,0,0.9)]"
                >
                  <path
                    d="M3.5 6L8 10.5L12.5 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : (
              <div className="size-10 flex justify-center items-center">
                {mobileMenuIcon || (
                  <div className="border-[1px] text-[#121c59]/80 border-[#121c59]/80 rounded-full size-10 flex justify-center items-center">
                    <Icon name="ellipsis" size="16" />
                  </div>
                )}
              </div>
            )}
          </Dropdown>
        )}
      </div>
    </div>
  );
}
